import { ChangeEvent, useCallback, useEffect, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'app/hooks'

import { selectedHomeSelector } from 'features/home/selectors'
import { updateLicenseNoteThunk } from 'features/licenses/slice'
import NoteCell from 'features/licenses/components/LicensesGroupsTable/Note/NoteCell'
import type { LicenseType } from 'features/licenses/types'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { fetchAvailableLicensesThunk } from '../../../slice'

interface AvailableLicenseNoteProps {
  original: LicenseType
  initialNote: string | null
}

function AvailableLicenseNote({
  initialNote,
  original,
}: AvailableLicenseNoteProps) {
  const dispatch = useAppDispatch()
  const rootDomainId = useAppSelector(currentRootDomainIdSelector)

  const [editMode, setEditMode] = useState(false)
  const [note, setNote] = useState<string>(initialNote || '')
  const [noteIsUpdating, setNoteIsUpdating] = useState(false)
  const [showError, setShowError] = useState(false)

  const currentHome = useAppSelector(selectedHomeSelector.data)
  const licenseId = original.id

  useEffect(() => {
    if (initialNote) {
      setNote(initialNote)
    }
  }, [initialNote])

  const handleEditModeClick = () => {
    setEditMode(!editMode)
  }

  const handleChange = (e: ChangeEvent<any>) => {
    setNote(e.target.value)
  }

  const handleSubmit = useCallback(async () => {
    if (rootDomainId && licenseId && currentHome) {
      setNoteIsUpdating(true)
      try {
        await dispatch(updateLicenseNoteThunk(rootDomainId, licenseId, note))
        await dispatch(
          fetchAvailableLicensesThunk(currentHome.id, currentHome.domainId)
        )
      } catch (error) {
        setShowError(true)
        console.log(`Error updating License Note with id: ${licenseId}`, error)
      } finally {
        setNoteIsUpdating(false)
        setEditMode(false)
      }
    }
  }, [dispatch, rootDomainId, licenseId, note, currentHome])

  const handleReject = () => {
    setNote(initialNote || '')
    setEditMode(false)
  }

  return (
    <NoteCell
      initialNote={initialNote}
      note={note}
      editMode={editMode}
      handleEditModeClick={handleEditModeClick}
      noteIsUpdating={noteIsUpdating}
      showError={showError}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      handleReject={handleReject}
      submitDisabled={!rootDomainId || !licenseId}
      usePermissionsCheck={false}
    />
  )
}

export default AvailableLicenseNote
